/**
 * Patient Summary — the native page's Application Designer descriptor.
 *
 * This is the manifest that used to be `SUMMARY_CARDS`, expressed as the
 * declaration any designable page makes. Patient Summary is the first page to
 * make it, and it is a FLOW page for the reason the architecture assessment
 * found: a Problem List card is as tall as the patient's problem list, so its
 * height belongs to the data and not to the designer. Forcing these cards onto a
 * fixed-row grid would clip one patient's card and leave dead space under
 * another's.
 *
 * `accessKey` names a gate the HOST evaluates on every render. It is not a
 * permission stored in a layout, and it is not the real protection either — the
 * native page decides what it fetches, and only hands over data it has already
 * permission-checked. The key is what stops an allowed-but-empty card from
 * occupying a column.
 *
 * The backend keeps its own copy of these ids in `backend/v3/patient_summary_layout.py`,
 * which is what makes server-side rejection of an unknown id real rather than
 * advisory. `backend/tests/test_patient_summary_layout.py` pins the two lists
 * equal, so the duplication cannot drift.
 */

import { LAYOUT_MODE_FLOW } from "@/lib/applicationDesigner/layoutModes";
import { definePageDescriptor, registerPageDescriptor } from "@/lib/applicationDesigner/pageDescriptor";

export const PATIENT_SUMMARY_PAGE = "patient-summary";
export const PATIENT_SUMMARY_COLUMNS = 3;

/**
 * The native cards, in declaration order, with the column and ordinal each one
 * takes when nobody has designed the page.
 */
export const PATIENT_SUMMARY_ELEMENTS = Object.freeze([
  { id: "problem-list", title: "Problem List", column: 0, order: 0, accessKey: "problemList" },
  { id: "medications", title: "Medications (Active)", column: 0, order: 1 },
  { id: "allergies", title: "Allergies", column: 0, order: 2, accessKey: "allergies" },
  { id: "recent-encounters", title: "Recent Encounters", column: 1, order: 0 },
  { id: "orders-results", title: "Recent Orders / Results", column: 1, order: 1 },
  { id: "health-maintenance", title: "Health Maintenance", column: 1, order: 2 },
  {
    id: "vital-signs",
    title: "Vital Signs (Most Recent)",
    column: 1,
    order: 3,
    /** Reference implementation: native-card box cosmetics, opt-in on this card only. */
    cosmetics: ["cardWidth", "cardHeight", "cardAlign", "cardNudgeY"],
  },
  { id: "patient-snapshot", title: "Patient Snapshot", column: 2, order: 0 },
  { id: "ai-patient-summary", title: "AI Patient Summary", column: 2, order: 1 },
  { id: "medical-history", title: "Medical History", column: 2, order: 2, accessKey: "medicalHistory" },
  { id: "surgical-history", title: "Surgical History", column: 2, order: 3, accessKey: "surgicalHistory" },
]);

export const patientSummaryDescriptor = registerPageDescriptor(
  definePageDescriptor({
    page: PATIENT_SUMMARY_PAGE,
    title: "Patient Summary",
    mode: LAYOUT_MODE_FLOW,
    columns: PATIENT_SUMMARY_COLUMNS,
    elements: PATIENT_SUMMARY_ELEMENTS,
    /** Phase 1 rearranges what the page already ships; it adds nothing. */
    componentRegistry: [],
  }),
);

export default patientSummaryDescriptor;
